import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const faqs = {
  es: [
    { keywords: ['servicios', 'ofrecen', 'hacen', 'haceis', 'servicio'], answer: 'Ofrecemos diseño web UI/UX, desarrollo web, tiendas online (ecommerce), mantenimiento informático, SEO técnico y consultoría tecnológica (transformación digital, data & BI, automatización e IA, desarrollo de software y optimización digital).' },
    { keywords: ['contacto', 'contactar', 'email', 'correo', 'hablar', 'comunicar'], answer: 'Puedes contactarnos a través del formulario de contacto en nuestra web o directamente al email: atencionalcliente@elmakinatech.com. Te responderemos en menos de 24 horas.' },
    { keywords: ['elmakinatech', 'ElmakinaTech', 'empresa', 'agencia', 'quienes', 'sois'], answer: 'ElmakinaTech es una agencia de diseño web, desarrollo web y SEO técnico. Creamos sitios que cargan rápido, se entienden bien y están preparados para crecer. Combinamos estrategia digital, diseño UI/UX, desarrollo moderno y SEO.' },
    { keywords: ['consultoría', 'consultoria', 'consultor', 'consulting'], answer: 'Sí, ofrecemos consultoría tecnológica en: transformación digital, data & business intelligence, automatización e IA, desarrollo de software a medida y optimización digital. Más información en nuestra sección de consultoría.' },
    { keywords: ['precio', 'precios', 'presupuesto', 'cuanto', 'cuesta', 'coste', 'tarifas'], answer: 'Cada proyecto es único. El precio depende del alcance, la complejidad y los requisitos específicos. Te recomiendo que nos contactes a través del formulario o email para recibir una propuesta personalizada.' },
    { keywords: ['seo', 'posicionamiento', 'google', 'buscador'], answer: 'Ofrecemos SEO técnico integral: optimización de velocidad, estructura de datos, meta tags, Core Web Vitals, arquitectura de información y estrategia de contenido. Todo para que tu web aparezca en los primeros resultados de Google.' },
    { keywords: ['tiempo', 'plazo', 'entrega', 'cuanto tarda'], answer: 'Dependiendo del proyecto, un primer entregable medible puede estar listo en 4-8 semanas. Proyectos más complejos pueden requerir más tiempo. Te daremos un cronograma detallado en la propuesta.' },
    { keywords: ['ecommerce', 'tienda', 'online', 'vender'], answer: 'Creamos tiendas online rápidas, fáciles de gestionar y optimizadas para vender. Catálogos claros, checkout optimizado, integración con CRM y SEO para ecommerce.' },
  ],
  en: [
    { keywords: ['services', 'offer', 'provide', 'service', 'do you'], answer: 'We offer UI/UX Web Design, Web Development, Ecommerce, IT Maintenance, Technical SEO, and Technology Consulting (digital transformation, data & BI, automation & AI, software development, and digital optimization).' },
    { keywords: ['contact', 'email', 'reach', 'talk', 'message'], answer: 'You can contact us through our website contact form or directly at: atencionalcliente@elmakinatech.com. We will respond within 24 hours.' },
    { keywords: ['elmakinatech', 'ElmakinaTech', 'company', 'agency', 'who', 'about'], answer: 'ElmakinaTech is a web design, web development, and technical SEO agency. We create fast-loading, clear, and scalable websites. We combine digital strategy, UI/UX design, modern development, and SEO.' },
    { keywords: ['consulting', 'consultancy', 'consultant'], answer: 'Yes, we offer technology consulting in: digital transformation, data & business intelligence, automation & AI, custom software development, and digital optimization. More info in our consulting section.' },
    { keywords: ['price', 'pricing', 'cost', 'budget', 'how much', 'quote', 'rates'], answer: 'Every project is unique. Pricing depends on scope, complexity, and specific requirements. Contact us through the form or email for a personalized proposal.' },
    { keywords: ['seo', 'ranking', 'google', 'search'], answer: 'We offer comprehensive technical SEO: speed optimization, data structure, meta tags, Core Web Vitals, information architecture, and content strategy. All to get your website on Google\'s first page.' },
    { keywords: ['time', 'timeline', 'delivery', 'how long'], answer: 'Depending on the project, a measurable first deliverable can be ready in 4-8 weeks. More complex projects may require additional time. We will provide a detailed timeline in the proposal.' },
    { keywords: ['ecommerce', 'store', 'online', 'sell', 'shop'], answer: 'We create fast, easy-to-manage online stores optimized to sell more. Clear catalogs, optimized checkout, CRM integration, and ecommerce SEO.' },
  ],
};

const greetings = {
  es: '¡Hola! Soy el asistente virtual de ElmakinaTech. ¿En qué puedo ayudarte?',
  en: 'Hi! I\'m ElmakinaTech\'s virtual assistant. How can I help you?',
};

function ChatBot() {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [showOptions, setShowOptions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ from: 'bot', text: greetings[lang] }]);
    }
  }, [open, lang, messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  const getResponse = (userMsg) => {
    const lower = userMsg.toLowerCase();
    const faqList = faqs[lang];
    for (const faq of faqList) {
      if (faq.keywords.some(kw => lower.includes(kw))) {
        return faq.answer;
      }
    }
    return lang === 'es'
      ? 'No tengo una respuesta exacta para eso. ¿Prefieres contactarnos directamente por email (atencionalcliente@elmakinatech.com) o probar con otra pregunta?'
      : 'I don\'t have an exact answer for that. Would you like to contact us directly at atencionalcliente@elmakinatech.com or try another question?';
  };

  const handleSend = (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setMessages(prev => [...prev, { from: 'user', text: msg }]);
    setInput('');
    setShowOptions(false);
    setTimeout(() => {
      const response = getResponse(msg);
      setMessages(prev => [...prev, { from: 'bot', text: response }]);
    }, 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  const quickQuestions = lang === 'es'
    ? ['¿Qué servicios ofrecen?', '¿Cómo contactarlos?', '¿Hacen SEO?']
    : ['What services do you offer?', 'How to contact you?', 'Do you do SEO?'];

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        aria-label="Abrir chat"
        title="Abrir chat"
        className="fixed bottom-6 right-24 z-[70] inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-container text-white shadow-lg shadow-black/30 transition-transform duration-200 hover:scale-105 hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-white/80 focus:ring-offset-2 focus:ring-offset-[#080f14]"
      >
        {open ? (
          <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-[70] w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl border border-primary/15 bg-[#131b2e] shadow-2xl shadow-black/50 overflow-hidden animate-[fadeIn_0.2s_ease-out]">
          <div className="flex items-center justify-between bg-primary-container/20 px-4 py-3 border-b border-primary/15">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-container text-white text-sm font-bold">
E
              </div>
              <div>
                <p className="text-white text-sm font-semibold">ElmakinaTech</p>
                <p className="text-[10px] text-on-surface-variant">Online</p>
              </div>
            </div>
            <button
              onClick={() => { setOpen(false); setMessages([]); setShowOptions(true); }}
              className="text-on-surface-variant hover:text-white transition-colors"
              aria-label="Cerrar chat"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="h-[380px] overflow-y-auto p-3 space-y-3 scrollbar-thin">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    msg.from === 'user'
                      ? 'bg-primary-container text-white rounded-br-md'
                      : 'bg-surface-container text-on-background rounded-bl-md'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {showOptions && messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(q)}
                    className="text-xs bg-surface-container-low hover:bg-surface-container-high text-on-surface-variant hover:text-white border border-white/10 rounded-xl px-3 py-1.5 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-primary/15 p-3 flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={lang === 'es' ? 'Escribe un mensaje...' : 'Type a message...'}
              className="flex-1 bg-surface-container-low text-white placeholder:text-on-surface-variant/50 rounded-xl px-4 py-2.5 text-sm border border-white/10 focus:outline-none focus:border-primary/50"
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim()}
              aria-label={lang === 'es' ? 'Enviar mensaje' : 'Send message'}
              className="bg-primary-container text-white rounded-xl px-3 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .scrollbar-thin::-webkit-scrollbar { width: 4px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #4d4354; border-radius: 4px; }
      `}</style>
    </>
  );
}

export default ChatBot;
